import { useEffect, useState } from "react";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { toast } from "sonner";
import { SkeletonRow } from "../../components/Skeleton";
import { t } from "../../lib/i18n";

const STAGES = [
  { key: "interested", label: t("Interesado") },
  { key: "nda", label: "NDA" },
  { key: "ioi", label: "IOI" },
  { key: "loi", label: "LOI" },
  { key: "dd", label: t("Due Diligence") },
  { key: "closing", label: t("Closing") },
];

export default function SearcherPipeline() {
  const [interests, setInterests] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const r = await api.get("/interests/mine");
      setInterests(r.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const moveTo = async (id, status) => {
    try {
      await api.patch(`/interests/${id}/status`, { status });
      toast.success(["ioi", "loi"].includes(status) ? "Oferta registrada: el vendedor ha sido notificado y se ha abierto su Etapa 7 (LOI)" : "Estado actualizado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error actualizando");
    }
  };

  const grouped = STAGES.reduce((acc, s) => {
    acc[s.key] = interests.filter((i) => i.status === s.key);
    return acc;
  }, {});

  return (
    <Layout>
      <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-12">
        <span className="font-mono-label text-gold">{t("Pipeline · Searcher")}</span>
        <h1 className="font-serif-display text-4xl md:text-5xl mt-3 mb-3">{t("Tu deal flow")}</h1>
        <p className="text-muted-foreground mb-10 max-w-xl">
          {t("Mueve tus oportunidades por las etapas del proceso de adquisición. Al pasar a IOI o LOI (requiere NDA aprobado e IM publicado) se abre la Etapa 7 · Oferta Indicativa en el panel del vendedor.")}
        </p>

        {loading ? (
          <div className="hairline border-r-0 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 overflow-x-auto" data-testid="pipeline-loading">
            {STAGES.map((s) => (
              <div key={s.key} className="hairline border-l-0 border-t-0 border-b-0 min-h-[400px]">
                <div className="px-4 py-3 hairline-b bg-bone flex items-center justify-between">
                  <span className="font-mono-label text-gold">{s.label}</span>
                  <span className="font-mono-label text-muted-foreground">—</span>
                </div>
                <div className="p-3 space-y-3">
                  <SkeletonRow />
                  <SkeletonRow />
                </div>
              </div>
            ))}
          </div>
        ) : interests.length === 0 ? (
          <div className="hairline p-12 text-center bg-card">
            <h3 className="font-serif-display text-2xl mb-2">{t("Aún no has expresado interés en ninguna empresa")}</h3>
            <Link to="/searcher/marketplace" className="font-mono-label text-gold inline-flex items-center gap-1" data-testid="pipeline-empty-go-market">
              {t("Ir al marketplace")}{" "}<ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        ) : (
          <div className="hairline border-r-0 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 overflow-x-auto">
            {STAGES.map((s) => (
              <div key={s.key} className="hairline border-l-0 border-t-0 border-b-0 min-h-[400px]">
                <div className="px-4 py-3 hairline-b bg-bone flex items-center justify-between">
                  <span className="font-mono-label text-gold">{s.label}</span>
                  <span className="font-mono-label text-muted-foreground">{grouped[s.key].length}</span>
                </div>
                <div className="p-3 space-y-3">
                  {grouped[s.key].map((it) => (
                    <PipelineCard key={it.interest_id} it={it} onMove={(status) => moveTo(it.interest_id, status)} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

const PipelineCard = ({ it, onMove }) => {
  const co = it.company || {};
  return (
    <div className="hairline p-3 bg-card text-sm space-y-2" data-testid={`pipeline-card-${it.interest_id}`}>
      <Link to={`/searcher/company/${it.company_id}`} className="block">
        <span className="font-mono-label text-muted-foreground">{co.sector}</span>
        <div className="font-serif-display text-lg leading-tight">{co.name_blind || co.sector}</div>
      </Link>
      {co.score != null && (
        <div className="font-mono-label text-gold">{t("Score ·")}{" "}{co.score}/100</div>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger className="font-mono-label flex items-center gap-1 text-[#1E3A5F] mt-1" data-testid={`pipeline-move-${it.interest_id}`}>
          {t("Mover")}{" "}<ChevronDown className="w-3 h-3" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="rounded-none">
          {STAGES.map((s) => (
            <DropdownMenuItem
              key={s.key}
              onClick={() => onMove(s.key)}
              disabled={s.key === it.status}
              className="rounded-none font-mono-label"
            >
              {s.label}
            </DropdownMenuItem>
          ))}
          <DropdownMenuItem
            onClick={() => onMove("declined")}
            className="rounded-none font-mono-label text-destructive"
          >
            {t("Descartar")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
